import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useApi, useLoad, errMsg, fmtTime } from "@/lib/api";
import { card } from "@/components/admin/kit";

const ST = { planned: "bg-slate-100", ready: "bg-sky-100", placing: "bg-amber-100", placed: "bg-green-100", retry: "bg-amber-100",
  failed: "bg-red-100", missed: "bg-red-100", skipped: "bg-gray-100", cancelled: "bg-gray-100" };

function JobRow({ j }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t py-2 text-sm" data-testid={`job-${j.time}-${j.to_masked}`}>
      <b className="w-14">{j.time}</b>
      <span className="w-28 text-muted-foreground">{j.to_masked}</span>
      <span className="flex-1">{j.items.map((i) => `${i.patient} · ${i.label}`).join("; ")}{j.items.length > 1 && <span className="ml-1 rounded-full bg-[var(--lav)] px-2 text-xs">merged</span>}{j.moved_from && <span className="ml-1 text-xs text-amber-700">moved from {j.moved_from} (quiet hours)</span>}</span>
      <span className={`rounded-full px-2 text-xs font-semibold ${ST[j.status] || ""}`} data-testid="job-status">{j.status}{j.call?.result && j.call.result !== "pending" ? ` · ${j.call.result.replace("_", " ")}` : ""}{j.call?.pressed_9 ? " · pressed 9" : ""}</span>
      {j.error && <span className="w-full text-xs text-muted-foreground">{j.error}</span>}
    </div>
  );
}

function RestartTest({ act, api }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[var(--lav)]/50 p-2 text-sm" data-testid="restart-test-box">
      <span className="font-semibold">After a press-9 test:</span>
      <label className="flex items-center gap-1"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="restart-consent-check" /> New consent given for my verified test number</label>
      <Button size="sm" disabled={!ok} onClick={() => act(async () => { const r = await api.post("/scheduler/restart-test-numbers", { consent: true }); setOk(false); return r; }, "Calls restarted for your test number")} data-testid="restart-test-btn">Restart calls to my test number</Button>
    </div>
  );
}

export function LiveCalls() {
  const api = useApi();
  const [st, reloadSt] = useLoad("/scheduler/status");
  const [jobs, reloadJobs] = useLoad("/scheduler/jobs");
  const reload = () => { reloadSt(); reloadJobs(); };
  useEffect(() => { const t = setInterval(() => { reloadSt(); reloadJobs(); }, 30000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!st) return null;
  return (
    <div className={`${card} mb-4 grid gap-3`} data-testid="live-calls">
      <div className="flex flex-wrap items-center gap-3">
        <p className="mr-auto font-bold">Live scheduled calls</p>
        <label className="flex items-center gap-2 text-sm font-semibold">Automatic calls
          <Switch checked={st.auto_calls_enabled} onCheckedChange={(on) => { if (on && !window.confirm("Turn ON automatic calls? Scheduled reminder calls will be dialled on time (test mode: verified numbers only).")) { return; } act(() => api.put("/scheduler/switch", { on }), on ? "Automatic calls ON" : "Automatic calls OFF"); }} data-testid="auto-calls-switch" />
        </label>
        <Button size="sm" variant="outline" onClick={() => act(() => api.post("/scheduler/plan"), "Plan refreshed")} data-testid="plan-now-btn"><RefreshCw size={14} /> Refresh plan</Button>
      </div>
      <p className={`flex items-center gap-2 text-sm ${st.watchdog_ok ? "text-green-700" : "text-red-700"}`} data-testid="scheduler-health">
        {st.watchdog_ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        {st.last_tick ? `Last check ${fmtTime(st.last_tick)} (${st.minutes_since_check} min ago, ${st.last_source}) · outside pinger: ${st.minutes_since_outside_check == null ? "never" : `${st.minutes_since_outside_check} min ago`}` : "Scheduler has not run yet"}
        {!st.real_patients_allowed && <span className="text-muted-foreground">· Test mode: only verified numbers are dialled</span>}
      </p>
      {st.alerts.map((a) => (
        <div key={a.id} className="flex items-center gap-2 rounded-xl bg-red-50 p-2 text-sm text-red-800" data-testid={`alert-${a.kind}`}>
          <AlertTriangle size={14} /><span className="flex-1">{a.text}</span>
          <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/scheduler/alerts/${a.id}/close`), "Alert closed")}>Close</Button>
        </div>
      ))}
      <RestartTest act={act} api={api} />
      <div>
        <p className="text-xs font-semibold text-muted-foreground">Today's call jobs ({(jobs || []).length})</p>
        {(jobs || []).map((j) => <JobRow key={j.id} j={j} />)}
      </div>
    </div>
  );
}
